import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card, Accordion, Badge } from 'react-bootstrap';
import * as Icon from 'react-feather';
import { mexicoCityToday } from '../../../utils/dateTime';

function DefaultModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const user = authUser();
    const today = mexicoCityToday();

    const [requestData, setRequestData] = useState({
        client_id: props.clientId || '',
        requested_by_user_id: user?.id || '',
        route_id: '',
        details: '',
        notes: ''
    });

    const [routes, setRoutes] = useState([]);
    const [stops, setStops] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (props.show && props.clientId) {
            loadRoutesForClient(props.clientId);
            setRequestData(prev => ({
                ...prev,
                client_id: props.clientId,
                requested_by_user_id: user?.id || ''
            }));
        }
    }, [props.show, props.clientId]);

    const resetForm = () => {
        setRequestData({
            client_id: props.clientId || '',
            requested_by_user_id: user?.id || '',
            route_id: '',
            details: '',
            notes: ''
        });
        setStops([]);
    };

    const loadRoutesForClient = async (clientId) => {
        try {
            const response = await axios.get(`${apiUrl}/finance/get_routes?client_id=${clientId}`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setRoutes(response.data.msg.Routes || []);
        } catch (error) {
            console.error('Error loading routes:', error);
            alert('Error al cargar rutas');
        }
    };

    const handleDataChange = (field, value) => {
        setRequestData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRouteChange = async (event) => {
        const routeId = event.target.value;
        setRequestData(prev => ({
            ...prev,
            route_id: routeId
        }));

        if (routeId) {
            try {
                const response = await axios.get(`${apiUrl}/finance/get_route_stops?route_id=${routeId}`, {
                    headers: {
                        Authorization: authHeader()
                    }
                });
                const routeStops = response.data.msg.Stops || [];

                // Initialize stops with default values
                const initializedStops = routeStops.map(routeStop => ({
                    ...routeStop,
                    origin_date: today,
                    origin_time: '08:00',
                    destination_date: today,
                    destination_time: '20:00',
                    load_type: 'Seco',
                    weight: 5
                }));

                setStops(initializedStops);
            } catch (error) {
                console.error('Error loading route stops:', error);
                alert('Error al cargar paradas de la ruta');
            }
        } else {
            setStops([]);
        }
    };

    const handleStopChange = (index, field, value) => {
        const updatedStops = [...stops];
        updatedStops[index] = {
            ...updatedStops[index],
            [field]: value
        };
        setStops(updatedStops);
    };

    const addStop = () => {
        const newStop = {
            origin_address: '',
            destination_address: '',
            origin_date: today,
            origin_time: '08:00',
            destination_date: today,
            destination_time: '20:00',
            load_type: 'Seco',
            weight: 5
        };
        setStops([...stops, newStop]);
    };

    const removeStop = (index) => {
        const updatedStops = stops.filter((_, i) => i !== index);
        setStops(updatedStops);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            // Calculate start and end dates from stops
            let requestedStartDate = today + ' 08:00';
            let requestedEndDate = today + ' 20:00';

            if (stops.length > 0) {
                requestedStartDate = stops[0].origin_date + ' ' + stops[0].origin_time;
                requestedEndDate = stops[stops.length - 1].destination_date + ' ' + stops[stops.length - 1].destination_time;
            }

            const payload = {
                client_id: parseInt(requestData.client_id),
                requested_by_user_id: parseInt(requestData.requested_by_user_id),
                route_id: requestData.route_id ? parseInt(requestData.route_id) : null,
                details: requestData.details,
                requested_start_date: requestedStartDate,
                requested_end_date: requestedEndDate,
                notes: requestData.notes,
                stops: stops.map(stop => ({
                    origin_address: stop.origin_address,
                    destination_address: stop.destination_address,
                    origin_date: stop.origin_date + ' ' + stop.origin_time,
                    destination_date: stop.destination_date + ' ' + stop.destination_time,
                    load_type: stop.load_type,
                    weight: stop.weight
                }))
            };

            await axios.post(`${apiUrl}/trips/add_trip_request`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();
            resetForm();
            alert('Solicitud de viaje creada exitosamente');
        } catch (error) {
            console.error('Error creating trip request:', error);
            alert('Error al crear la solicitud de viaje');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal {...props} size="xl" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    <Icon.Send size={20} className="me-2" />
                    Nueva Solicitud de Viaje
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    <Form onSubmit={handleSubmit}>
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información de la Solicitud</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Ruta *</Form.Label>
                                        <Form.Select
                                            value={requestData.route_id}
                                            onChange={handleRouteChange}
                                            required
                                        >
                                            <option value="">Seleccionar ruta...</option>
                                            {routes.map((route) => (
                                                <option key={route.id} value={route.id}>
                                                    {route.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Tipo de Carga *</Form.Label>
                                        <Form.Control
                                            value={requestData.details}
                                            onChange={(e) => handleDataChange('details', e.target.value)}
                                            placeholder="Ej: Pimientos secos, Cartón, etc."
                                            required
                                        />
                                    </Form.Group>
                                </Row>

                                <Row>
                                    <Form.Group as={Col}>
                                        <Form.Label>Notas Adicionales</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={requestData.notes}
                                            onChange={(e) => handleDataChange('notes', e.target.value)}
                                            placeholder="Cualquier información adicional sobre la solicitud..."
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Trip Stops */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Paradas del Viaje</h6>
                            </Card.Header>
                            <Card.Body>
                                {stops.length > 0 && (
                                    <Accordion>
                                        {stops.map((stop, index) => (
                                            <Accordion.Item eventKey={index.toString()} key={index}>
                                                <Accordion.Header>
                                                    <span>
                                                        Parada {index + 1}: {stop.origin_address || 'Nueva parada'} → {stop.destination_address || 'Destino'}
                                                    </span>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    {/* Trip Route Card */}
                                                    <div className="mb-4">
                                                        <Card className="border-0 bg-light">
                                                            <Card.Body className="py-3">
                                                                <Row className="align-items-center">
                                                                    <Col md={4} className="text-center">
                                                                        <div className="mb-2">
                                                                            <h6 className="mb-1 text-primary">Origen</h6>
                                                                            <div className="fw-bold fs-5">{stop.origin_date || today}</div>
                                                                            <div className="text-muted small">{stop.origin_time || '08:00'}</div>
                                                                        </div>
                                                                        <Form.Control
                                                                            value={stop.origin_address || ''}
                                                                            onChange={(e) => handleStopChange(index, 'origin_address', e.target.value)}
                                                                            placeholder="Dirección de origen"
                                                                            className="text-center border-0 bg-transparent fw-semibold"
                                                                            style={{ fontSize: '0.9rem' }}
                                                                        />
                                                                    </Col>

                                                                    <Col md={4} className="text-center">
                                                                        <div className="d-flex align-items-center justify-content-center">
                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                            <div className="mx-3">
                                                                                <Icon.Truck size={24} className="text-primary" />
                                                                            </div>
                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                        </div>
                                                                        <div className="mt-2">
                                                                            <Badge bg="warning">Pendiente</Badge>
                                                                        </div>
                                                                    </Col>

                                                                    <Col md={4} className="text-center">
                                                                        <div className="mb-2">
                                                                            <h6 className="mb-1 text-success">Destino</h6>
                                                                            <div className="fw-bold fs-5">{stop.destination_date || today}</div>
                                                                            <div className="text-muted small">{stop.destination_time || '20:00'}</div>
                                                                        </div>
                                                                        <Form.Control
                                                                            value={stop.destination_address || ''}
                                                                            onChange={(e) => handleStopChange(index, 'destination_address', e.target.value)}
                                                                            placeholder="Dirección de destino"
                                                                            className="text-center border-0 bg-transparent fw-semibold"
                                                                            style={{ fontSize: '0.9rem' }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </div>

                                                    {/* Date and Time Controls */}
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <h6 className="text-primary mb-2">
                                                                <Icon.MapPin size={16} className="me-1" />
                                                                Horarios de Carga
                                                            </h6>
                                                            <Row>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Fecha</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={stop.origin_date || today}
                                                                        onChange={(e) => handleStopChange(index, 'origin_date', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Hora</Form.Label>
                                                                    <Form.Control
                                                                        type="time"
                                                                        value={stop.origin_time || '08:00'}
                                                                        onChange={(e) => handleStopChange(index, 'origin_time', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                            </Row>
                                                        </Col>
                                                        <Col md={6}>
                                                            <h6 className="text-success mb-2">
                                                                <Icon.Flag size={16} className="me-1" />
                                                                Horarios de Descarga
                                                            </h6>
                                                            <Row>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Fecha</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={stop.destination_date || today}
                                                                        onChange={(e) => handleStopChange(index, 'destination_date', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Hora</Form.Label>
                                                                    <Form.Control
                                                                        type="time"
                                                                        value={stop.destination_time || '20:00'}
                                                                        onChange={(e) => handleStopChange(index, 'destination_time', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                            </Row>
                                                        </Col>
                                                    </Row>

                                                    {/* Additional Details */}
                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={6}>
                                                            <Form.Label>Tipo de Carga</Form.Label>
                                                            <Form.Select
                                                                value={stop.load_type || 'Seco'}
                                                                onChange={(e) => handleStopChange(index, 'load_type', e.target.value)}
                                                            >
                                                                <option value="Seco">Seco</option>
                                                                <option value="Comida">Comida</option>
                                                                <option value="Otro">Otro</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={6}>
                                                            <Form.Label>Peso (ton)</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                max="50"
                                                                step="0.1"
                                                                value={stop.weight || 5}
                                                                onChange={(e) => handleStopChange(index, 'weight', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                )}
                                {stops.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <p>Selecciona una ruta para cargar las paradas automáticamente</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <div className="text-end">
                            <Button variant="secondary" onClick={props.onHide} className="me-2" type="button">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Enviando...' : 'Enviar Solicitud'}
                            </Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let add_new_request = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    variant="primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    <Icon.Plus size={16} className="me-1" />
                    Nueva Solicitud
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={add_new_request}
                    setData={this.props.setData}
                    clientId={this.props.clientId}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
